import React, { useMemo } from "react";

import { css } from "@emotion/css";
import { CellContext, type SortingFn } from "@tanstack/react-table";
import { useTranslation } from "react-i18next";

import { Icons } from "@/assets/icons.ts";
import { useStyles } from "@/hooks/useStyles.ts";
import { TelemonitoringTags } from "@/models/CarePlanModel.ts";
import {
  telemonitoringTagColorMapping,
  telemonitoringTagTranslationMapping,
  PatientListItem,
} from "@/models/PatientSummaryModel.ts";
import { userFormattedName } from "@/models/UserModel.ts";
import { Theme } from "@/theme/CreateTheme.ts";
import { Tag } from "@/uiKit/atoms/Tag.tsx";
import { Typography } from "@/uiKit/atoms/Typography.tsx";

export type NursePatientNameColumnProps = CellContext<PatientListItem, string>;

export const NursePatientNameColumn: React.FC<NursePatientNameColumnProps> = ({ row }) => {
  const { t } = useTranslation();
  const styles = useStyles(makeStyles);
  const { id, familyName, givenName, tags, hasPendingReinclusionSuggestion } = row.original;

  // Show one of the following tags: 'expiring_soon', 'expired_recently', 'no_glycemia_data', 'nurse_asked_for_reinclusion', 'expired' (only if 'nurse_asked_for_reinclusion' is present)
  const tagsToDisplay: TelemonitoringTags[] = useMemo(() => {
    const tagsGenerated = () => {
      const tagsToAdd: TelemonitoringTags[] = [];
      if (tags.includes("nurse_asked_for_reinclusion")) {
        tagsToAdd.push("nurse_asked_for_reinclusion");
      }

      if (tags.includes("expiring_soon")) {
        tagsToAdd.push("expiring_soon");
        return tagsToAdd;
      }
      if (tags.includes("expired_recently")) {
        tagsToAdd.push("expired_recently");
        return tagsToAdd;
      }
      if (tags.includes("no_glycemia_data") && (tags.includes("active") || tags.includes("gestational_no_insulin"))) {
        tagsToAdd.push("no_glycemia_data");
        return tagsToAdd;
      }

      if (tags.includes("expired") && tags.includes("nurse_asked_for_reinclusion")) {
        tagsToAdd.push("expired");
        return tagsToAdd;
      }

      return tagsToAdd;
    };

    return tagsGenerated().sort((a, b) => {
      if (a === "nurse_asked_for_reinclusion") return 1;
      if (b === "nurse_asked_for_reinclusion") return -1;
      return 0;
    });
  }, [tags]);

  return (
    <div className={styles.container}>
      <Typography className={styles.name} variant="body">
        {userFormattedName(familyName, givenName)}
      </Typography>
      <div className={styles.tag}>
        {tagsToDisplay.map(tag => (
          <Tag
            key={`${id}${tag}`}
            label={t(telemonitoringTagTranslationMapping(tag))}
            color={telemonitoringTagColorMapping(tag)}
            icon={tag === "nurse_asked_for_reinclusion" ? <Icons.stethoscope className={styles.icon} /> : undefined}
          />
        ))}
        {hasPendingReinclusionSuggestion && (
          <Tag
            key={`${id}reinclusion-suggestion`}
            label={t("models.reinclusionSuggestion.pendingTag")}
            color="purple"
            icon={<Icons.aiStars className={styles.icon} />}
          />
        )}
      </div>
    </div>
  );
};

export const NursePatientNameHeader: React.FC = () => {
  const { t } = useTranslation();
  const styles = useStyles(makeHeaderStyles);
  return (
    <div className={styles.container}>
      <Typography>{t("pages.patients.columns.patientName")}</Typography>
    </div>
  );
};

export const sortByNursePatientName: SortingFn<PatientListItem> = (rowA, rowB) =>
  userFormattedName(rowA.original.familyName, rowA.original.givenName).localeCompare(
    userFormattedName(rowB.original.familyName, rowB.original.givenName),
  );

const makeStyles = (theme: Theme) => ({
  container: css`
    display: flex;
    flex-direction: row;
    align-items: center;
    flex-wrap: wrap;
    padding-left: ${theme.spacing(8)};
    gap: ${theme.spacing(8)};
  `,
  name: css`
    font-weight: bold;
    text-wrap: wrap;
  `,
  tag: css({
    display: "flex",
    flexDirection: "row",
    gap: theme.spacing(2),
    flexWrap: "wrap",
  }),
  icon: css({
    padding: theme.spacing(0.5),
    width: theme.typography.body.fontSize,
    height: theme.typography.body.fontSize,
  }),
});

const makeHeaderStyles = (theme: Theme) => ({
  container: css({
    display: "flex",
    flexDirection: "row",
    justifyContent: "flex-start",
    paddingLeft: theme.spacing(8),
  }),
});
